import { Button, IconButton, TextField, Tooltip } from '@ads/components-react';
import {
  ArrowPathIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface TemporaryPasswordModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (password: string) => void;
  readonly isLoading?: boolean;
  readonly isSuccess?: boolean;
}

const PASSWORD_CHARS =
  'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';

function generateRandomPassword(): string {
  return Array.from(
    { length: 12 },
    () => PASSWORD_CHARS[Math.floor(Math.random() * PASSWORD_CHARS.length)],
  ).join('');
}

/**
 * Modal para definir uma senha temporária para um usuário.
 * Exibe formulário de entrada/geração e, após confirmação bem-sucedida (`isSuccess=true`),
 * mostra a senha definida com botão de cópia para área de transferência.
 *
 * @param open - Controla a visibilidade do modal
 * @param onClose - Callback para fechar o modal
 * @param onConfirm - Callback com a senha definida (sempre uma string não-vazia)
 * @param isLoading - Desabilita os botões durante a requisição
 * @param isSuccess - Quando true, exibe a view de sucesso com a senha e botão de cópia
 */
export function TemporaryPasswordModal({
  open,
  onClose,
  onConfirm,
  isLoading = false,
  isSuccess = false,
}: TemporaryPasswordModalProps) {
  const { t } = useTranslation('personManagement');
  const [password, setPassword] = useState('');
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (open) return;
    const id = setTimeout(() => {
      document.body.style.removeProperty('pointer-events');
    }, 0);
    return () => clearTimeout(id);
  }, [open]);

  const handleClose = () => {
    setPassword('');
    setIsCopied(false);
    onClose();
  };

  const handleGenerate = () => {
    setPassword(generateRandomPassword());
  };

  const handleConfirm = () => {
    onConfirm(password.trim());
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(password).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft ![animation:none]" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none ![animation:none]"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-xs py-mini">
            <Dialog.Title className="ts-heading-sm font-semibold text-ctx-content-title">
              {t('enrollmentManagement.tempPasswordModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.close')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-xxs px-xs py-mini">
            {isSuccess ? (
              <div className="flex items-center justify-between rounded-sm border-thin border-ctx-layout-border bg-ctx-layout-body px-mini py-nano">
                <span className="ts-paragraph-xs break-all font-mono text-ctx-content-base">
                  {password}
                </span>
                <Tooltip
                  title={
                    isCopied
                      ? t('enrollmentManagement.tempPasswordModal.copied')
                      : t('actions.copy')
                  }
                  side="top"
                  custom={{ root: 'z-[9999]' }}
                >
                  <IconButton
                    icon={isCopied ? ClipboardDocumentCheckIcon : ClipboardDocumentIcon}
                    hierarchy="tertiary"
                    size="md"
                    ariaLabel={t('actions.copy')}
                    onClick={handleCopy}
                  />
                </Tooltip>
              </div>
            ) : (
              <div className="flex items-end gap-mini">
                <div className="flex-1">
                  <TextField
                    label={t('enrollmentManagement.tempPasswordModal.label')}
                    placeholder={t('enrollmentManagement.tempPasswordModal.placeholder')}
                    value={password}
                    size="md"
                    onChange={e => setPassword(e.target.value)}
                  />
                </div>
                <Tooltip
                  title={t('enrollmentManagement.tempPasswordModal.generatePassword')}
                  side="top"
                  custom={{ root: 'z-[9999]' }}
                >
                  <IconButton
                    icon={ArrowPathIcon}
                    hierarchy="secondary"
                    size="md"
                    ariaLabel={t('enrollmentManagement.tempPasswordModal.generatePassword')}
                    onClick={handleGenerate}
                  />
                </Tooltip>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-xs border-t-hairline border-ctx-layout-border px-md py-xs">
            {isSuccess ? (
              <Button hierarchy="primary" size="lg" onClick={handleClose}>
                {t('actions.close')}
              </Button>
            ) : (
              <>
                <Button hierarchy="secondary" size="lg" onClick={handleClose}>
                  {t('enrollmentManagement.tempPasswordModal.cancel')}
                </Button>
                <Button
                  hierarchy="primary"
                  size="lg"
                  isLoading={isLoading}
                  disabled={isLoading || !password.trim()}
                  onClick={handleConfirm}
                >
                  {t('enrollmentManagement.tempPasswordModal.confirm')}
                </Button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
